import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { Btn, TextField, SelectField, FileUpload, Label, Loading, ErrorState } from "./ui";

const Group = ({ title, children }) => (
  <div className="border border-[#292929] p-6 mb-6">
    <div className="text-xs tracking-widest uppercase text-muted-eff mb-5">{title}</div>
    <div className="grid md:grid-cols-2 gap-5">{children}</div>
  </div>
);

export default function SettingsPanel() {
  const [s, setS] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    setS(null);
    api.get("/admin/settings").then(({ data }) => setS(data || {})).catch(() => setError(true));
  };
  useEffect(() => { load(); }, []);
  if (error) return <ErrorState onRetry={load} />;
  if (!s) return <Loading />;

  const upd = (path) => (v) => {
    setS((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) { ref[keys[i]] = { ...(ref[keys[i]] || {}) }; ref = ref[keys[i]]; }
      ref[keys[keys.length - 1]] = v;
      return next;
    });
  };
  const g = (path) => path.split(".").reduce((o, k) => (o ? o[k] : undefined), s);

  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("Réglages enregistrés"); }
    catch { toast.error("Erreur"); }
  };

  const hero = s.hero || {};
  const socials = s.socials || [];
  const setSocial = (i, k, v) => { const arr = [...socials]; arr[i] = { ...arr[i], [k]: v }; upd("socials")(arr); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display text-3xl">Général & Hero</h2>
        <Btn data-testid="settings-save" variant="solid" onClick={save}>Enregistrer</Btn>
      </div>

      <Group title="Identité">
        <TextField label="Nom / marque" value={g("brand")} onChange={upd("brand")} />
        <TextField label="Slogan" value={g("slogan")} onChange={upd("slogan")} />
        <TextField label="Tagline" value={g("tagline")} onChange={upd("tagline")} />
        <TextField label="Message central" value={g("central_message")} onChange={upd("central_message")} textarea />
        <FileUpload label="Logo SVG / image" kind="logo" value={g("logo_svg")} onChange={upd("logo_svg")} />
        <FileUpload label="Vidéo d'identité (MP4/WEBM)" kind="video" value={g("identity_video")} onChange={upd("identity_video")} />
        <FileUpload label="Poster vidéo d'identité" kind="image" value={g("identity_video_poster")} onChange={upd("identity_video_poster")} />
      </Group>

      <Group title="Hero — Image / Vidéo">
        <SelectField label="Mode" value={hero.mode} onChange={upd("hero.mode")}
          options={[{ value: "image", label: "Image statique" }, { value: "video", label: "Vidéo" }]} />
        <TextField label="Titre" value={hero.title} onChange={upd("hero.title")} />
        <TextField label="Sous-titre" value={hero.subtitle} onChange={upd("hero.subtitle")} />
        <TextField label="CTA label" value={hero.cta_label} onChange={upd("hero.cta_label")} />
        <TextField label="CTA lien" value={hero.cta_href} onChange={upd("hero.cta_href")} />
        <FileUpload label="Image desktop" kind="image" value={hero.image_desktop} onChange={upd("hero.image_desktop")} />
        <FileUpload label="Image mobile (option)" kind="image" value={hero.image_mobile} onChange={upd("hero.image_mobile")} />
        <FileUpload label="Vidéo desktop" kind="video" value={hero.video_url} onChange={upd("hero.video_url")} />
        <FileUpload label="Vidéo mobile (option)" kind="video" value={hero.video_mobile} onChange={upd("hero.video_mobile")} />
        <FileUpload label="Poster vidéo" kind="image" value={hero.poster} onChange={upd("hero.poster")} />
        <FileUpload label="Fallback image mobile" kind="image" value={hero.fallback_image} onChange={upd("hero.fallback_image")} />
        <SelectField label="Autoplay" value={String(hero.autoplay !== false)} onChange={(v) => upd("hero.autoplay")(v === "true")} options={[{ value: "true", label: "Oui" }, { value: "false", label: "Non" }]} />
        <SelectField label="Muted" value={String(hero.muted !== false)} onChange={(v) => upd("hero.muted")(v === "true")} options={[{ value: "true", label: "Oui" }, { value: "false", label: "Non" }]} />
        <SelectField label="Loop" value={String(hero.loop !== false)} onChange={(v) => upd("hero.loop")(v === "true")} options={[{ value: "true", label: "Oui" }, { value: "false", label: "Non" }]} />
      </Group>

      <Group title="Manifeste">
        <TextField label="Titre" value={g("manifesto.title")} onChange={upd("manifesto.title")} />
        <div />
        <TextField label="Texte" value={g("manifesto.text")} onChange={upd("manifesto.text")} textarea />
        <TextField label="Sous-texte" value={g("manifesto.subtext")} onChange={upd("manifesto.subtext")} textarea />
      </Group>

      <Group title="Collaborer">
        <TextField label="Eyebrow" value={g("collaborate.eyebrow")} onChange={upd("collaborate.eyebrow")} />
        <TextField label="Titre" value={g("collaborate.title")} onChange={upd("collaborate.title")} />
        <TextField label="Texte" value={g("collaborate.text")} onChange={upd("collaborate.text")} textarea />
      </Group>

      <Group title="Contact & Formulaires">
        <TextField label="Email de contact" value={g("contact.email")} onChange={upd("contact.email")} />
        <TextField label="Téléphone" value={g("contact.phone")} onChange={upd("contact.phone")} />
        <TextField label="Adresse" value={g("contact.address")} onChange={upd("contact.address")} />
        <TextField label="Destinataire des formulaires (email)" value={g("form_recipient")} onChange={upd("form_recipient")} />
      </Group>

      <Group title="SEO">
        <TextField label="Title" value={g("seo.title")} onChange={upd("seo.title")} />
        <TextField label="Slug" value={g("seo.slug")} onChange={upd("seo.slug")} />
        <TextField label="Meta description" value={g("seo.description")} onChange={upd("seo.description")} textarea />
        <TextField label="Mots-clés" value={g("seo.keywords")} onChange={upd("seo.keywords")} textarea />
        <FileUpload label="Image Open Graph" kind="image" value={g("seo.og_image")} onChange={upd("seo.og_image")} />
        <SelectField label="Indexation" value={String(g("seo.index") !== false)} onChange={(v) => upd("seo.index")(v === "true")} options={[{ value: "true", label: "Autorisée" }, { value: "false", label: "Bloquée" }]} />
      </Group>

      <div className="border border-[#292929] p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div className="text-xs tracking-widest uppercase text-muted-eff">Réseaux sociaux</div>
          <Btn variant="ghost" onClick={() => upd("socials")([...socials, { platform: "", url: "" }])}><Plus className="w-3.5 h-3.5 inline" /> Ajouter</Btn>
        </div>
        <div className="space-y-3">
          {socials.map((so, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-4"><TextField label="Plateforme" value={so.platform} onChange={(v) => setSocial(i, "platform", v)} /></div>
              <div className="col-span-7"><TextField label="URL" value={so.url} onChange={(v) => setSocial(i, "url", v)} /></div>
              <button className="col-span-1 text-muted-eff hover:text-white pb-3" onClick={() => upd("socials")(socials.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <Group title="Footer">
        <TextField label="Texte" value={g("footer.text")} onChange={upd("footer.text")} />
        <TextField label="Note" value={g("footer.note")} onChange={upd("footer.note")} />
      </Group>

      <Btn data-testid="settings-save-2" variant="solid" onClick={save}>Enregistrer les réglages</Btn>
    </div>
  );
}
